import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { 
  Play, Pause, SkipBack, SkipForward, 
  ChevronLeft, ChevronRight, Repeat, 
  Gauge, Eye, EyeOff 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, ReferenceLine
} from "recharts";

export default function TimeLapsePlayer({ framesData, frameResults, nFrames }) {
  const [currentFrame, setCurrentFrame] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(5); // fps
  const [loop, setLoop] = useState(true);
  const [showRed, setShowRed] = useState(true);
  const [showGreen, setShowGreen] = useState(true);
  const [showOverlay, setShowOverlay] = useState(true);
  
  const redCanvasRef = useRef(null);
  const greenCanvasRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const animationRef = useRef(null);
  const lastTimeRef = useRef(0);

  // Draw frame on canvas
  const drawFrame = useCallback((frameIdx) => {
    if (!framesData || !framesData[frameIdx]) return;
    
    const frame = framesData[frameIdx];
    const { width, height, red, green } = frame;
    
    // Draw red channel
    if (redCanvasRef.current && showRed) {
      const ctx = redCanvasRef.current.getContext("2d");
      redCanvasRef.current.width = width;
      redCanvasRef.current.height = height;
      
      const imageData = ctx.createImageData(width, height);
      for (let i = 0; i < red.length; i++) {
        const idx = i * 4;
        imageData.data[idx] = red[i];
        imageData.data[idx + 1] = 0;
        imageData.data[idx + 2] = 0;
        imageData.data[idx + 3] = 255;
      }
      ctx.putImageData(imageData, 0, 0);
    }
    
    // Draw green channel
    if (greenCanvasRef.current && showGreen) {
      const ctx = greenCanvasRef.current.getContext("2d");
      greenCanvasRef.current.width = width;
      greenCanvasRef.current.height = height;
      
      const imageData = ctx.createImageData(width, height);
      for (let i = 0; i < green.length; i++) {
        const idx = i * 4;
        imageData.data[idx] = 0;
        imageData.data[idx + 1] = green[i];
        imageData.data[idx + 2] = 0;
        imageData.data[idx + 3] = 255;
      }
      ctx.putImageData(imageData, 0, 0);
    }
    
    // Draw overlay (combined)
    if (overlayCanvasRef.current && showOverlay) {
      const ctx = overlayCanvasRef.current.getContext("2d");
      overlayCanvasRef.current.width = width;
      overlayCanvasRef.current.height = height;
      
      const imageData = ctx.createImageData(width, height);
      for (let i = 0; i < red.length; i++) {
        const idx = i * 4;
        imageData.data[idx] = showRed ? red[i] : 0;
        imageData.data[idx + 1] = showGreen ? green[i] : 0;
        imageData.data[idx + 2] = 0;
        imageData.data[idx + 3] = 255;
      }
      ctx.putImageData(imageData, 0, 0);
    }
  }, [framesData, showRed, showGreen, showOverlay]);

  // Animation loop
  useEffect(() => {
    if (!isPlaying) return;
    
    const interval = 1000 / playbackSpeed;
    
    const animate = (timestamp) => {
      if (timestamp - lastTimeRef.current >= interval) {
        lastTimeRef.current = timestamp;
        
        setCurrentFrame(prev => {
          if (prev >= nFrames - 1) {
            if (loop) return 0;
            setIsPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, playbackSpeed, loop, nFrames]);

  // Draw current frame
  useEffect(() => {
    drawFrame(currentFrame);
  }, [currentFrame, drawFrame]);

  // Chart data
  const chartData = useMemo(() => {
    if (!frameResults) return [];
    return frameResults.map((r, i) => ({
      frame: i,
      pearson: r.pearson_r,
      m1: r.manders_m1,
      m2: r.manders_m2
    }));
  }, [frameResults]);

  const currentMetrics = frameResults?.[currentFrame];

  const handleFrameChange = ([value]) => {
    setCurrentFrame(value);
    if (isPlaying) setIsPlaying(false);
  };

  const stepFrame = (direction) => {
    setCurrentFrame(prev => {
      const next = prev + direction;
      if (next < 0) return loop ? nFrames - 1 : 0;
      if (next >= nFrames) return loop ? 0 : nFrames - 1;
      return next;
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
      data-testid="timelapse-player"
    >
      {!framesData && (
        <div className="metric-card p-6">
          <p className="text-sm text-muted-foreground">
            Frame previews were not returned by the API (disabled to reduce payload). Re-run the analysis with
            <span className="font-medium"> Include frame previews</span> enabled in settings.
          </p>
        </div>
      )}

      {/* Main Player Area */}
      {framesData && (
      <div className="metric-card p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Red Channel */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <Label className="text-sm">Red Channel</Label>
              </div>
              <button
                onClick={() => setShowRed(!showRed)}
                className="text-muted-foreground hover:text-foreground"
                data-testid="toggle-red-timelapse"
              >
                {showRed ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
            </div>
            <div className="relative aspect-square bg-black/50 rounded-lg overflow-hidden glow-red">
              <canvas
                ref={redCanvasRef}
                className="w-full h-full object-contain"
                style={{ imageRendering: "pixelated" }}
              />
            </div>
          </div>

          {/* Green Channel */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <Label className="text-sm">Green Channel</Label>
              </div>
              <button
                onClick={() => setShowGreen(!showGreen)}
                className="text-muted-foreground hover:text-foreground"
                data-testid="toggle-green-timelapse"
              >
                {showGreen ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
            </div>
            <div className="relative aspect-square bg-black/50 rounded-lg overflow-hidden glow-green">
              <canvas
                ref={greenCanvasRef}
                className="w-full h-full object-contain"
                style={{ imageRendering: "pixelated" }}
              />
            </div>
          </div>

          {/* Overlay */}
          <div className="space-y-2">
            <div className="flex items-center">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <Label className="text-sm">Composite Overlay</Label>
              </div>
            </div>
            <div className="relative aspect-square bg-black/50 rounded-lg overflow-hidden">
              <canvas
                ref={overlayCanvasRef}
                className="w-full h-full object-contain"
                style={{ imageRendering: "pixelated" }}
              />
            </div>
          </div>
        </div>

        {/* Playback Controls */}
        <div className="mt-6 space-y-4">
          {/* Timeline Scrubber */}
          <div className="flex items-center gap-4">
            <span className="font-mono text-sm text-muted-foreground min-w-[4rem]">
              {currentFrame + 1} / {nFrames}
            </span>
            <Slider
              value={[currentFrame]}
              onValueChange={handleFrameChange}
              max={nFrames - 1}
              step={1}
              className="flex-1"
              data-testid="frame-scrubber"
            />
          </div>

          {/* Control Buttons */}
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCurrentFrame(0)}
              className="rounded-full"
              data-testid="goto-start-btn"
            >
              <SkipBack className="w-4 h-4" />
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={() => stepFrame(-1)}
              className="rounded-full"
              data-testid="prev-frame-btn"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            
            <Button
              size="icon"
              onClick={() => setIsPlaying(!isPlaying)}
              className="w-12 h-12 rounded-full"
              data-testid="play-pause-btn"
            >
              {isPlaying ? (
                <Pause className="w-5 h-5" />
              ) : (
                <Play className="w-5 h-5 ml-0.5" />
              )}
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={() => stepFrame(1)}
              className="rounded-full"
              data-testid="next-frame-btn"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCurrentFrame(nFrames - 1)}
              className="rounded-full"
              data-testid="goto-end-btn"
            >
              <SkipForward className="w-4 h-4" />
            </Button>

            <div className="w-px h-8 bg-white/10 mx-2" />

            <Button
              variant={loop ? "secondary" : "ghost"}
              size="icon"
              onClick={() => setLoop(!loop)}
              className="rounded-full"
              data-testid="loop-btn"
            >
              <Repeat className="w-4 h-4" />
            </Button>

            <div className="flex items-center gap-2 ml-4">
              <Gauge className="w-4 h-4 text-muted-foreground" />
              <Slider
                value={[playbackSpeed]}
                onValueChange={([v]) => setPlaybackSpeed(v)}
                min={1}
                max={30}
                step={1}
                className="w-24"
                data-testid="speed-slider"
              />
              <span className="font-mono text-xs text-muted-foreground min-w-[3rem]">
                {playbackSpeed} fps
              </span>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Metrics Over Time Chart */}
      <div className="metric-card p-6">
        <h3 className="font-chivo text-lg font-semibold mb-4">Metrics Over Time</h3>
        
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
              <XAxis 
                dataKey="frame" 
                stroke="#64748b"
                tick={{ fill: "#64748b", fontSize: 11 }}
              />
              <YAxis 
                stroke="#64748b"
                tick={{ fill: "#64748b", fontSize: 11 }}
                domain={[0, 1]}
              />
              <Tooltip
                contentStyle={{
                  background: "rgba(15, 23, 42, 0.9)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: "8px"
                }}
              />
              <ReferenceLine x={currentFrame} stroke="#3b82f6" strokeDasharray="3 3" />
              <Line
                type="monotone"
                dataKey="pearson"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={false}
                name="Pearson r"
              />
              <Line
                type="monotone"
                dataKey="m1"
                stroke="#ef4444"
                strokeWidth={2}
                dot={false}
                name="Manders M1"
              />
              <Line
                type="monotone"
                dataKey="m2"
                stroke="#22c55e"
                strokeWidth={2}
                dot={false}
                name="Manders M2"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Current Frame Metrics */}
        {currentMetrics && (
          <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-white/5">
            <MetricStat 
              label="Pearson r" 
              value={currentMetrics.pearson_r.toFixed(4)} 
              color="blue"
            />
            <MetricStat 
              label="Manders M1" 
              value={currentMetrics.manders_m1.toFixed(4)} 
              color="red"
            />
            <MetricStat 
              label="Manders M2" 
              value={currentMetrics.manders_m2.toFixed(4)} 
              color="green"
            />
          </div>
        )}
      </div>
    </motion.div>
  );
}

function MetricStat({ label, value, color }) {
  const colorClasses = {
    blue: "text-blue-400",
    red: "text-red-400",
    green: "text-green-400"
  };

  return (
    <div className="text-center">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p className={`font-mono text-xl font-bold ${colorClasses[color]}`}>
        {value}
      </p>
    </div>
  );
}
